import {
  AdminPanelSettings,
  AssignmentTurnedIn,
  BookmarkAdded,
  Cancel,
  CardGiftcard,
  CheckCircle,
  Description,
  ErrorOutline,
  EventAvailable,
  EventBusy,
  GroupAdd,
  GroupRemove,
  HowToReg,
  LinkOff,
  MarkEmailUnread,
  PauseCircle,
  Payment,
  PersonAddAlt1,
  PersonRemove,
  Restore,
  Work,
  WorkHistory,
} from "@mui/icons-material";
import MarkAsUnreadIcon from "@mui/icons-material/MarkAsUnread";
import WifiTetheringOffIcon from "@mui/icons-material/WifiTetheringOff";
import { ENotificationTargetKind } from "./notificationTargetKind";

export { ENotificationTargetKind };

export const NOTIFICATION_ICONS = {
  sucessfullConnect: <HowToReg htmlColor="#fc9700" />,
  confirmationConnect: <MarkAsUnreadIcon htmlColor="#fc9700" />,
  noResponseConnect: <WifiTetheringOffIcon htmlColor="#fc9700" />,
  planSubscription: <CheckCircle htmlColor="#fc9700" />,
  planSubscriptionRestored: <Restore htmlColor="#fc9700" />,
  planSubscriptionCancelled: <Cancel htmlColor="#fc9700" />,
  planSubscriptionSuspended: <PauseCircle htmlColor="#fc9700" />,
  planSubscriptionFailed: <ErrorOutline htmlColor="#fc9700" />,
  freeTrialActivated: <CardGiftcard htmlColor="#fc9700" />,
  sendContactInfo: <MarkEmailUnread htmlColor="#fc9700" />,
  recievedContactInfo: <Description htmlColor="#fc9700" />,
  jobAssigned: <Work htmlColor="#fc9700" />,
  jobInProgress: <WorkHistory htmlColor="#fc9700" />,
  jobAccepted: <BookmarkAdded htmlColor="#fc9700" />,
  jobDeclined: <Cancel htmlColor="#fc9700" />,
  jobCompleted: <AssignmentTurnedIn htmlColor="#fc9700" />,
  jobScheduled: <EventAvailable htmlColor="#fc9700" />,
  jobCancelled: <EventBusy htmlColor="#fc9700" />,
  JobPunched: <WorkHistory htmlColor="#fc9700" />,
  quoteAccepted: <BookmarkAdded htmlColor="#fc9700" />,
  paypalOnboardingCompleted: <Payment htmlColor="#fc9700" />,
  invoicePaid: <Payment htmlColor="#fc9700" />,
  invoicePaidDeposit: <Payment htmlColor="#fc9700" />,
  InvoiceNotifiacton: <Payment htmlColor="#fc9700" />,
  wokforceDataDeletionRequest: <GroupRemove htmlColor="#fc9700" />,
  adminInvitationReceived: <HowToReg htmlColor="#fc9700" />,
  sendAdminInvitation: <AdminPanelSettings htmlColor="#fc9700" />,
  sendWorkforceInvitation: <PersonAddAlt1 htmlColor="#fc9700" />,
  acceptedWorkforceInvitation: <GroupAdd htmlColor="#fc9700" />,
  acceptedAdminInvitation: <HowToReg htmlColor="#fc9700" />,
  deletedAdmin: <PersonRemove htmlColor="#fc9700" />,
  connectionCanceled: <LinkOff htmlColor="#fc9700" />,
};

const INVOICE_NOTIFICATION_TYPES = new Set(["invoicePaid", "invoicePaidDeposit", "InvoiceNotifiacton"]);
const INBOUND_MESSAGE_NOTIFICATION_TYPE = "inboundMessageReceived";
const INTERNAL_MESSAGE_NOTIFICATION_TYPE = "internalMessageReceived";
const MESSAGE_GROUP_NOTIFICATION_TYPES = new Set(["internalGroupMessageReceived", "internalGroupMentionReceived"]);

const isJobNotificationType = (type) => /^job/i.test(type ?? "");

export const isInboundMessageNotification = (notification) => notification?.type === INBOUND_MESSAGE_NOTIFICATION_TYPE;

export const parseNotificationMeta = (meta) => {
  if (!meta) return {};
  if (typeof meta === "string") {
    try {
      return JSON.parse(meta);
    } catch {
      return {};
    }
  }
  return meta;
};

export const getNotificationTarget = (notification) => {
  const meta = parseNotificationMeta(notification?.meta);
  const type = notification?.type;

  if (isJobNotificationType(type)) {
    const id = meta.id ?? meta.jobId;
    if (id == null) return null;
    return { kind: ENotificationTargetKind.Job, id };
  }

  if (type === "quoteAccepted") {
    const id = meta.id ?? meta.quoteId;
    if (id == null) return null;
    return { kind: ENotificationTargetKind.Quote, id };
  }

  if (INVOICE_NOTIFICATION_TYPES.has(type)) {
    const id = meta.uid ?? meta.id;
    if (id == null) return null;
    return { kind: ENotificationTargetKind.Invoice, id };
  }

  if (type === "adminInvitationReceived") {
    const status = String(meta?.status || "").toLowerCase();
    if (["accepted", "canceled"].includes(status)) return null;

    const id = meta.uid ?? meta.id;
    if (id == null) return null;
    return { kind: ENotificationTargetKind.AdminInvitation, id };
  }

  if (type === "acceptedAdminInvitation") {
    return { kind: ENotificationTargetKind.ManageAdmins };
  }

  return null;
};

export const getNotificationCompanyId = (notification) => {
  const meta = parseNotificationMeta(notification?.meta);
  return notification?.companyId ?? meta?.companyId ?? meta?.company?.id;
};

const getDirectChatTarget = (target) => {
  if (!target || typeof target !== "object") return null;
  if (target.type === "job" && target.jobEventUid) return target;
  if (target.type === "sms" && target.phoneNumber) return target;
  if (target.type === "internal" && target.contactId != null) return { ...target, contactId: String(target.contactId) };
  if (target.type === "group" && target.groupUid) return target;
  return null;
};

export const getNotificationChatTarget = (notification) => {
  const meta = parseNotificationMeta(notification?.meta);
  const type = notification?.type;
  const directTarget = getDirectChatTarget(meta?.target ?? meta?.chatTarget);
  if (directTarget) return directTarget;

  const companyId = getNotificationCompanyId(notification);

  if (MESSAGE_GROUP_NOTIFICATION_TYPES.has(type) && meta?.groupUid) {
    return { type: "group", groupUid: String(meta.groupUid), companyId };
  }

  const jobEventUid = meta?.jobEventUid ?? meta?.eventUid ?? meta?.jobEvent?.uid;
  if (jobEventUid) return { type: "job", jobEventUid, companyId };

  if (type === INBOUND_MESSAGE_NOTIFICATION_TYPE) {
    const phoneNumber = meta?.phoneNumber ?? meta?.contactPhone ?? meta?.from ?? meta?.senderPhoneNumber;
    const sessionNumber = meta?.sessionNumber ?? meta?.to ?? meta?.companyPhoneNumber ?? meta?.fromNumber;
    if (phoneNumber) return { type: "sms", phoneNumber, companyId, sessionNumber };
  }

  if (type === INTERNAL_MESSAGE_NOTIFICATION_TYPE) {
    const contactId = meta?.contactId ?? meta?.senderContactId;
    if (contactId != null) return { type: "internal", contactId: String(contactId), contact: meta?.contact ?? null };
  }

  return null;
};

export const hasNotificationAction = (notificationType) => {
  if (!notificationType) return false;
  if (isJobNotificationType(notificationType)) return true;
  if (notificationType === "quoteAccepted") return true;
  if (INVOICE_NOTIFICATION_TYPES.has(notificationType)) return true;
  if (notificationType === "adminInvitationReceived") return true;
  if (notificationType === "acceptedAdminInvitation") return true;
  return false;
};

export const hasNotificationActionFor = (notification) => Boolean(getNotificationTarget(notification) || getNotificationChatTarget(notification));

export const shouldShowNotification = (notification) => {
  if (notification.isRead) return false;

  if (notification.type !== "adminInvitationReceived") return true;

  const meta = parseNotificationMeta(notification?.meta);
  const status = String(meta?.status || "").toLowerCase();
  return !["accepted", "canceled"].includes(status);
};

export const getNotificationCompany = (notification, companies = []) => {
  const companyId = getNotificationCompanyId(notification);
  if (companyId == null) return null;

  const membership = companies.find((item) => String(item?.companyId ?? item?.company?.id ?? item?.id) === String(companyId));
  return membership?.company ?? membership ?? null;
};

export const getInboundMessageSenderCompany = (notification, companies = []) => {
  const meta = parseNotificationMeta(notification?.meta);
  const company = meta?.senderCompany || meta?.fromCompany || meta?.sourceCompany;
  if (company) return company;

  const senderCompanyId = meta?.senderCompanyId || meta?.fromCompanyId || meta?.sourceCompanyId;
  if (senderCompanyId == null) return null;

  const membership = companies.find((item) => String(item?.companyId ?? item?.company?.id ?? item?.id) === String(senderCompanyId));
  return membership?.company ?? membership ?? null;
};

const getMessageMetaText = (meta) => {
  const content = meta?.content;
  return meta?.body || meta?.messageText || meta?.text || meta?.message || content?.body || content?.message || content?.text || content?.Body || "";
};

const parseMessageFromText = (message = "") => {
  const match = String(message).match(/New message from\s+([^:]+):\s*(.*)$/i);
  if (!match) return {};
  return { from: match[1]?.trim(), text: match[2]?.trim() };
};

export const getNotificationChatMeta = (notification, companies = []) => {
  const meta = parseNotificationMeta(notification?.meta);
  const parsedMessage = parseMessageFromText(notification?.message);
  const focusMessageUid = meta?.messageUid ?? meta?.uid ?? meta?.message?.uid ?? null;
  const focusMessageText = getMessageMetaText(meta) || parsedMessage.text || null;
  const company = getNotificationCompany(notification, companies) || getInboundMessageSenderCompany(notification, companies);
  const companyName =
    meta?.companyName ||
    meta?.senderCompanyName ||
    meta?.fromCompanyName ||
    meta?.sourceCompanyName ||
    company?.businessName ||
    company?.name ||
    "";
  const chatName = meta?.chatName || meta?.senderName || meta?.from || meta?.contactName || parsedMessage.from || notification?.message || "";

  return { companyName, chatName, focusMessageUid, focusMessageText };
};

export const getNotificationDisplayMessage = (notification, companies = []) => {
  if (!isInboundMessageNotification(notification)) return notification?.message ?? "";

  const meta = parseNotificationMeta(notification?.meta);
  const parsedMessage = parseMessageFromText(notification?.message);
  const company = getInboundMessageSenderCompany(notification, companies);
  const companyName = meta?.senderCompanyName || meta?.fromCompanyName || meta?.sourceCompanyName || company?.businessName || company?.name;
  const sender = meta?.from || meta?.contactPhone || meta?.phoneNumber || parsedMessage.from;
  const text = getMessageMetaText(meta) || parsedMessage.text;

  if (sender && companyName && text) return `New message from ${sender} at ${companyName}: ${text}`;
  if (sender && companyName) return `New message from ${sender} at ${companyName}`;
  if (companyName && text) return `New message at ${companyName}: ${text}`;
  if (companyName) return `New message at ${companyName}`;

  return notification?.message ?? "";
};

export const getAccountNotificationDisplayMessage = (notification, companies = []) => {
  const message = getNotificationDisplayMessage(notification, companies);
  const company = getNotificationCompany(notification, companies);
  const companyName = company?.businessName || company?.name;

  if (!companyName || !message) return message;

  return `To ${companyName}: ${message}`;
};

export const getAccountNotificationDisplayParts = (notification, companies = []) => {
  const message = getNotificationDisplayMessage(notification, companies);
  const company = getNotificationCompany(notification, companies);
  const companyName = company?.businessName || company?.name;

  if (!companyName || !message) return { prefix: "", message };

  return { prefix: `To ${companyName}: `, message };
};

export const getUniqueNotificationTargets = (notifications) => {
  const seen = new Set();
  const result = [];

  for (const notification of notifications) {
    const target = getNotificationTarget(notification);
    if (!target) continue;

    const key =
      target.kind === ENotificationTargetKind.ManageAdmins ? `${ENotificationTargetKind.ManageAdmins}:organization` : `${target.kind}:${target.id}`;
    if (seen.has(key)) continue;

    seen.add(key);
    result.push(target);
  }

  return result;
};

export const getNotificationTriggerId = (target) => {
  if (target.kind === ENotificationTargetKind.Quote) return `open-quote-${target.id}`;
  if (target.kind === ENotificationTargetKind.Job) return `open-job-${target.id}`;
  if (target.kind === ENotificationTargetKind.Invoice) return `open-invoice-${target.id}`;
  if (target.kind === ENotificationTargetKind.ManageAdmins) return "open-manage-admins";
  return null;
};

export const triggerNotificationTarget = (target) => {
  const triggerId = getNotificationTriggerId(target);
  if (!triggerId) return false;

  document.getElementById(triggerId)?.click();
  return true;
};

const getStableMetaSignature = (meta) => {
  const parsed = parseNotificationMeta(meta);
  const keys = Object.keys(parsed);
  if (!keys.length) return "";

  return keys
    .sort()
    .map((key) => `${key}=${typeof parsed[key] === "object" ? JSON.stringify(parsed[key]) : parsed[key]}`)
    .join("&");
};

/**
 * Builds a content signature so notifications that are truly identical (same type, message and
 * meta) collapse into one, even when the backend persisted them as separate rows with different
 * ids. Chat messages keep their own row because their meta carries a unique messageUid.
 */
const getNotificationContentSignature = (notification) =>
  `${notification?.type ?? ""}|${notification?.message ?? ""}|${getStableMetaSignature(notification?.meta)}`;

// Sirve para filtrar invoices duplicados y elegir el que se puede presionar o tiene mas prioridad.
const getNotificationDedupeSignature = (notification) => {
  if (INVOICE_NOTIFICATION_TYPES.has(notification?.type)) {
    const meta = parseNotificationMeta(notification?.meta);
    const companyId = getNotificationCompanyId(notification) ?? "";
    const invoiceId = meta?.uid ?? meta?.id ?? meta?.identifierId ?? "";

    return `${notification?.type ?? ""}|invoice|${companyId}|${invoiceId || notification?.message || ""}`;
  }

  return getNotificationContentSignature(notification);
};

const getNotificationDedupePriority = (notification) => {
  let priority = 0;
  if (getNotificationTarget(notification)) priority += 2;
  if (!notification?.isRead) priority += 1;
  return priority;
};

export const dedupeNotificationsById = (notifications) => {
  const seenIds = new Set();
  const seenContent = new Map();
  const result = [];

  for (const notification of notifications) {
    if (notification?.id != null) {
      if (seenIds.has(notification.id)) continue;
      seenIds.add(notification.id);
    }

    const contentKey = getNotificationDedupeSignature(notification);
    const existingIndex = seenContent.get(contentKey);

    if (existingIndex != null) {
      const existing = result[existingIndex];
      if (getNotificationDedupePriority(notification) > getNotificationDedupePriority(existing)) {
        result[existingIndex] = notification;
      }
      continue;
    }

    seenContent.set(contentKey, result.length);

    result.push(notification);
  }

  return result;
};
